/**
 * RendPleX Portfolio Stress Test™ — Premium page.
 *
 * Route: /stress-test
 * Product intent: "What could happen to my portfolio if financial
 * conditions get worse?"
 *
 * All math runs server-side via `run_stress_test` (same engine as
 * `calculate_deal_metrics`). This page never computes financials on
 * the client — it just renders what the backend returns.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Sparkles, ShieldCheck, AlertTriangle, TrendingDown, TrendingUp,
  Play, Lock, Loader2, Info, Zap, HeartPulse, Target, Compass,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import LanguageSwitcher from '../components/LanguageSwitcher';
import DarkModeToggle from '../components/DarkModeToggle';
import { RendPlexLogo } from '../components/RendPlexLogo';
import { Footer } from '../components/LegalDisclaimers';
import PortfolioAwareAdvisor from '../components/PortfolioAwareAdvisor';
import { fireChatAnalytics } from '../lib/chatAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ANALYTICS_KEY = '__stress_test__';

const fmt$ = (n) => (n === null || n === undefined || Number.isNaN(n)
  ? '—' : (n < 0 ? `-$${Math.abs(Math.round(n)).toLocaleString()}` : `$${Math.round(n).toLocaleString()}`));
const fmtPct = (n, d = 1) => (n === null || n === undefined || Number.isNaN(n) ? '—' : `${Number(n).toFixed(d)}%`);
const fmtDec = (n, d = 2) => (n === null || n === undefined || Number.isNaN(n) ? '—' : Number(n).toFixed(d));

const CUSTOM_DEFAULT = { interest_rate_delta_pct: 2, vacancy_rate_absolute: 8, expense_delta_pct: 10, rent_delta_pct: -5 };

export default function StressTest() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [preset, setPreset] = useState('moderate');
  const [customFactors, setCustomFactors] = useState(CUSTOM_DEFAULT);
  const [presets, setPresets] = useState(null);
  const [result, setResult] = useState(null);
  const [breakingPoints, setBP] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    fireChatAnalytics('stress_test_viewed', ANALYTICS_KEY, {});
    axios.get(`${BACKEND_URL}/api/stress-test/presets`).then((r) => setPresets(r.data.presets)).catch(() => {});
    // Auto-run Moderate on mount so users see instant value
    runStress('moderate', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runStress = async (chosenPreset, factors) => {
    setLoading(true); setErrored(false);
    try {
      const body = chosenPreset ? { preset: chosenPreset } : { factors };
      const resp = await axios.post(`${BACKEND_URL}/api/stress-test/run`, body, { withCredentials: true });
      if (resp.data.empty) {
        setResult(resp.data);
      } else {
        setResult(resp.data);
        fireChatAnalytics('stress_test_run', ANALYTICS_KEY, {
          preset: chosenPreset, vulnerable: resp.data.counts.vulnerable, score: resp.data.resilience.score,
        });
      }
      // Fetch breaking points in parallel
      axios.post(`${BACKEND_URL}/api/stress-test/breaking-point`, {}, { withCredentials: true })
        .then((r) => setBP(r.data.breaking_points)).catch(() => {});
    } catch (e) {
      if (e.response?.status === 401) {
        // Anonymous users are not allowed on this Premium page — send them home.
        navigate('/');
        return;
      }
      if (e.response?.status === 403) setLocked(true);
      else setErrored(true);
    } finally {
      setLoading(false);
    }
  };

  const onPresetClick = (p) => {
    setPreset(p);
    fireChatAnalytics('stress_test_preset_selected', ANALYTICS_KEY, { preset: p });
    if (p === 'custom') return;
    runStress(p, null);
  };

  const onRunCustom = () => {
    setPreset('custom');
    fireChatAnalytics('stress_test_custom_created', ANALYTICS_KEY, customFactors);
    runStress(null, customFactors);
  };

  if (locked) return <PageShell><Locked t={t} navigate={navigate} /></PageShell>;

  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <button data-testid="stress-back" onClick={() => navigate('/portfolio')}
              className="text-sm text-slate-500 hover:text-[#12355B] dark:hover:text-white inline-flex items-center gap-1 mb-3">
              <ArrowLeft className="w-4 h-4" /> {t('stressTest.back')}
            </button>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#12355B] to-[#1565C0] flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 data-testid="stress-title" className="font-primary font-extrabold text-2xl md:text-3xl text-[#12355B] dark:text-white tracking-tight">
                    {t('stressTest.title')}
                  </h1>
                  <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-[#12355B] text-white dark:bg-[#45C04A] dark:text-[#12355B]">Premium</span>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{t('stressTest.subtitle')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Step 1 — Choose Scenario */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824] p-5 mb-4">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-3">{t('stressTest.chooseScenario')}</p>
          <div className="flex flex-wrap gap-2">
            {['mild', 'moderate', 'severe'].map((p) => (
              <button key={p} data-testid={`preset-${p}`} onClick={() => onPresetClick(p)}
                className={`px-4 h-10 rounded-xl text-sm font-semibold border transition-colors ${
                  preset === p ? 'bg-[#12355B] text-white border-[#12355B]' : 'bg-white dark:bg-slate-800/40 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-slate-300'}`}>
                {t(`stressTest.presets.${p}`)}
              </button>
            ))}
            <button data-testid="preset-custom" onClick={() => onPresetClick('custom')}
              className={`px-4 h-10 rounded-xl text-sm font-semibold border transition-colors ${
                preset === 'custom' ? 'bg-[#12355B] text-white border-[#12355B]' : 'bg-white dark:bg-slate-800/40 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-slate-300'}`}>
              {t('stressTest.presets.custom')}
            </button>
          </div>

          {preset === 'custom' && (
            <div data-testid="custom-factors" className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <FactorInput data-testid="factor-rate" label={t('stressTest.factors.rate') + ' †'} suffix="%" value={customFactors.interest_rate_delta_pct}
                onChange={(v) => setCustomFactors({ ...customFactors, interest_rate_delta_pct: v })} />
              <FactorInput data-testid="factor-vacancy" label={t('stressTest.factors.vacancy')} suffix="%" value={customFactors.vacancy_rate_absolute}
                onChange={(v) => setCustomFactors({ ...customFactors, vacancy_rate_absolute: v })} />
              <FactorInput data-testid="factor-expenses" label={t('stressTest.factors.expenses')} suffix="%" value={customFactors.expense_delta_pct}
                onChange={(v) => setCustomFactors({ ...customFactors, expense_delta_pct: v })} />
              <FactorInput data-testid="factor-rent" label={t('stressTest.factors.rent')} suffix="%" value={customFactors.rent_delta_pct}
                onChange={(v) => setCustomFactors({ ...customFactors, rent_delta_pct: v })} />
              <div className="sm:col-span-2 lg:col-span-4">
                <Button data-testid="run-custom" onClick={onRunCustom} disabled={loading}
                  className="w-full bg-[#12355B] hover:bg-[#1565C0] text-white rounded-xl h-11">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                  {t('stressTest.runStress')}
                </Button>
              </div>
              <p data-testid="hypothetical-rate-note" className="sm:col-span-2 lg:col-span-4 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                † {t('stressTest.hypotheticalRateNote')}
              </p>
            </div>
          )}
        </section>

        {loading && !result && <LoadingSkeleton />}
        {errored && <Failed t={t} onRetry={() => runStress(preset === 'custom' ? null : preset, customFactors)} />}

        {result && result.empty && (
          <EmptyPortfolio t={t} navigate={navigate} />
        )}

        {result && !result.empty && (
          <>
            {/* Step 3 — Results */}
            <ResilienceCard result={result} t={t} />
            <PortfolioBeforeAfter portfolio={result.portfolio} counts={result.counts} t={t} />
            {result.factor_attribution?.length > 0 && (
              <FactorAttribution items={result.factor_attribution} t={t} />
            )}
            {breakingPoints && <BreakingPointCard bp={breakingPoints} t={t} />}
            <PropertiesBreakdown properties={result.properties} t={t} navigate={navigate} />

            {/* Actions row */}
            <div className="flex flex-wrap gap-2 justify-center my-6">
              <Button data-testid="stress-doctor" variant="outline" onClick={() => navigate('/portfolio-doctor')}
                className="rounded-xl h-10">
                <HeartPulse className="w-4 h-4 mr-2" /> {t('stressTest.openDoctor')}
              </Button>
              <Button data-testid="stress-portfolio" variant="outline" onClick={() => navigate('/portfolio')}
                className="rounded-xl h-10">
                <Compass className="w-4 h-4 mr-2" /> {t('stressTest.openPortfolio')}
              </Button>
            </div>

            {/* Disclaimer */}
            <div data-testid="stress-disclaimer" className="rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/30 p-4 flex gap-3">
              <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs leading-relaxed text-amber-800 dark:text-amber-300">{t('stressTest.disclaimer')}</p>
            </div>
          </>
        )}
        <Footer />
      </div>
      {/* Portfolio-Aware AI Advisor — grounded on the currently rendered
          stress scenario. Only mounted once a result exists so the LLM
          receives the exact resilience/factor/vulnerable-property values
          the user is looking at. */}
      {result && !result.empty && (
        <PortfolioAwareAdvisor
          contextType="stress_test"
          context={{
            scenario_label: t(`stressTest.presets.${preset}`, { defaultValue: preset }),
            resilience_score: result.resilience?.score,
            factors: result.applied_factors,
            portfolio_before: {
              monthly_cash_flow: result.portfolio?.monthly_cash_flow_before,
              noi: result.portfolio?.noi_before,
            },
            portfolio_after: {
              monthly_cash_flow: result.portfolio?.monthly_cash_flow_after,
              noi: result.portfolio?.noi_after,
            },
            vulnerable_properties: (result.properties || [])
              .filter((p) => p.status === 'vulnerable')
              .slice(0, 3)
              .map((p) => ({
                address: p.address,
                monthly_cash_flow: p.monthly_cash_flow_after,
                dscr: p.dscr_after,
              })),
          }}
        />
      )}
    </PageShell>
  );
}

/* ================= Sub-components ================= */

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0E1A]">
      <header className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <RendPlexLogo />
          <div className="flex items-center gap-3">
            <DarkModeToggle />
            <LanguageSwitcher />
          </div>
        </div>
      </header>
      {children}
    </div>
  );
}

function FactorInput({ label, suffix, value, onChange, ...rest }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2">
        <Input type="number" step="0.5" value={value} onChange={(e) => onChange(Number(e.target.value))} className="rounded-xl h-10" {...rest} />
        <span className="text-slate-400 text-sm">{suffix}</span>
      </div>
    </div>
  );
}

function ResilienceCard({ result, t }) {
  const r = result.resilience;
  const band = r.band;
  const color = band === 'resilient' ? 'from-emerald-500 to-emerald-600' : band === 'moderate' ? 'from-amber-500 to-amber-600' : 'from-red-500 to-red-600';
  return (
    <section data-testid="resilience-card" className="rounded-2xl bg-gradient-to-br from-slate-900 to-[#12355B] text-white p-6 mb-4 shadow-card">
      <div className="flex items-center gap-6 flex-wrap">
        <div className={`w-24 h-24 rounded-full bg-gradient-to-br ${color} flex items-center justify-center flex-shrink-0`}>
          <div className="text-center">
            <div data-testid="resilience-score" className="text-3xl font-extrabold leading-none">{r.score}</div>
            <div className="text-[10px] tracking-widest">/ 100</div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-4 h-4" />
            <p className="text-[10px] uppercase tracking-widest font-bold text-white/60">{t('stressTest.resilience.label')}</p>
          </div>
          <h2 className="font-primary font-extrabold text-xl md:text-2xl">{t(`stressTest.resilience.band.${band}`)}</h2>
          <div className="grid grid-cols-3 gap-4 mt-4">
            {['cf_preservation', 'positive_cf_share', 'dscr_share'].map((k) => (
              <div key={k}>
                <p className="text-[9px] uppercase tracking-wider text-white/50 font-bold">{t(`stressTest.resilience.components.${k}`)}</p>
                <p className="text-lg font-bold">
                  {r.components?.[k] === undefined ? '—' : `${Number(r.components[k]).toFixed(0)}%`}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PortfolioBeforeAfter({ portfolio, counts, t }) {
  const p = portfolio;
  const rows = [
    { label: t('stressTest.metrics.monthlyCF'), before: p.monthly_cash_flow_baseline, after: p.monthly_cash_flow_stressed, isMoney: true },
    { label: t('stressTest.metrics.annualCF'), before: p.annual_cash_flow_baseline, after: p.annual_cash_flow_stressed, isMoney: true },
    { label: t('stressTest.metrics.noi'), before: p.noi_baseline, after: p.noi_stressed, isMoney: true },
  ];
  return (
    <section data-testid="portfolio-before-after" className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824] p-5 mb-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">{t('stressTest.portfolioTitle')}</p>
        <div className="flex gap-2">
          {counts.vulnerable > 0 && <Pill color="red" testid="count-vulnerable">{counts.vulnerable} {t('stressTest.status.vulnerable')}</Pill>}
          {counts.watch > 0 && <Pill color="amber" testid="count-watch">{counts.watch} {t('stressTest.status.watch')}</Pill>}
          {counts.resilient > 0 && <Pill color="emerald" testid="count-resilient">{counts.resilient} {t('stressTest.status.resilient')}</Pill>}
        </div>
      </div>
      <div className="space-y-2">
        {rows.map((r) => {
          const delta = r.after - r.before;
          const positive = delta >= 0;
          return (
            <div key={r.label} className="grid grid-cols-4 gap-2 items-center text-sm">
              <span className="text-slate-500 dark:text-slate-400">{r.label}</span>
              <span className="font-mono text-slate-500">{fmt$(r.before)}</span>
              <span className="font-mono font-bold text-[#12355B] dark:text-white">{fmt$(r.after)}</span>
              <span className={`text-xs font-bold ${positive ? 'text-emerald-500' : 'text-red-500'}`}>
                {positive ? <TrendingUp className="w-3 h-3 inline mr-1" /> : <TrendingDown className="w-3 h-3 inline mr-1" />}
                {positive ? '+' : ''}{fmt$(delta)}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function FactorAttribution({ items, t }) {
  return (
    <section data-testid="factor-attribution" className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824] p-5 mb-4">
      <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-3">{t('stressTest.attributionTitle')}</p>
      <p className="text-xs text-slate-500 mb-3">{t('stressTest.attributionSubtitle')}</p>
      <div className="space-y-2">
        {items.map((it, idx) => (
          <div key={it.factor} className="flex items-center gap-3">
            <span className="text-[10px] font-bold text-slate-400 w-6">#{idx + 1}</span>
            <span className="text-sm text-slate-700 dark:text-slate-300 flex-1">{t(`stressTest.factors.${it.factor}`)}</span>
            <span className="text-sm font-bold text-red-500">{fmt$(it.impact_monthly_cf)}{t('stressTest.perMonthSuffix')}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function BreakingPointCard({ bp, t }) {
  const rows = [
    { key: 'interest_rate', label: t('stressTest.breakingPoint.interestRate'), suffix: `% ${t('stressTest.breakingPoint.aboveBaseline')}`, abs: false },
    { key: 'vacancy', label: t('stressTest.breakingPoint.vacancy'), suffix: '%', abs: false },
    { key: 'expenses', label: t('stressTest.breakingPoint.expenses'), suffix: `% ${t('stressTest.breakingPoint.increase')}`, abs: false },
    // Rent search range is 0 → -60% (returns a negative value on convergence).
    // Show it as a positive magnitude alongside the 'decline' wording.
    { key: 'rent', label: t('stressTest.breakingPoint.rent'), suffix: `% ${t('stressTest.breakingPoint.decline')}`, abs: true },
  ];
  return (
    <section data-testid="breaking-points" className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824] p-5 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-amber-500" />
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">{t('stressTest.breakingPoint.title')}</p>
      </div>
      <p className="text-xs text-slate-500 mb-4">{t('stressTest.breakingPoint.subtitle')}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {rows.map((r) => {
          const entry = bp[r.key];
          return (
            <div key={r.key} data-testid={`bp-${r.key}`} className="rounded-xl bg-slate-50 dark:bg-slate-800/50 p-3">
              <p className="text-xs text-slate-500 mb-1">{r.label}</p>
              {entry?.feasible ? (
                <p className="text-base font-bold text-[#12355B] dark:text-white">
                  ~{fmtDec(r.abs ? Math.abs(entry.value) : entry.value, 1)}{r.suffix}
                </p>
              ) : (
                <p className="text-xs text-slate-400 italic">{t('stressTest.breakingPoint.notFeasible')}</p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function PropertiesBreakdown({ properties, t, navigate }) {
  return (
    <section data-testid="properties-breakdown" className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824] p-5 mb-4">
      <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-3">{t('stressTest.propertiesTitle')}</p>
      <div className="space-y-2">
        {properties.map((p) => {
          const dot = p.status === 'vulnerable' ? 'bg-red-500' : p.status === 'watch' ? 'bg-amber-500' : 'bg-emerald-500';
          return (
            <div key={p.deal_id} data-testid={`property-row-${p.deal_id}`}
              className="flex flex-wrap items-center gap-3 border border-slate-100 dark:border-slate-800 rounded-xl p-3">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#12355B] dark:text-white truncate">{p.address}</p>
                <p className="text-[11px] text-slate-400">
                  {fmt$(p.baseline.monthly_cash_flow)} → <span className={p.stressed.monthly_cash_flow < 0 ? 'text-red-500 font-bold' : 'text-slate-700 dark:text-slate-300 font-bold'}>{fmt$(p.stressed.monthly_cash_flow)}</span>{t('stressTest.perMonthSuffix')}
                  {p.baseline.dscr !== null && (
                    <> · DSCR {fmtDec(p.baseline.dscr)} → {fmtDec(p.stressed.dscr)}</>
                  )}
                </p>
              </div>
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                p.status === 'vulnerable' ? 'bg-red-500/10 text-red-500' :
                p.status === 'watch' ? 'bg-amber-500/10 text-amber-500' :
                'bg-emerald-500/10 text-emerald-500'
              }`}>
                {t(`stressTest.status.${p.status}`)}
              </span>
              <div className="flex gap-1.5">
                <Button size="sm" variant="ghost" data-testid={`view-${p.deal_id}`}
                  onClick={() => { fireChatAnalytics('stress_test_property_opened', ANALYTICS_KEY, { deal_id: p.deal_id }); navigate(`/deal/${p.deal_id}`); }}
                  className="h-8 text-xs">{t('stressTest.viewProperty')}</Button>
                {p.status !== 'resilient' && (
                  <Button size="sm" variant="outline" data-testid={`optimizer-${p.deal_id}`}
                    onClick={() => {
                      fireChatAnalytics('stress_test_rent_optimizer_opened', ANALYTICS_KEY, { deal_id: p.deal_id });
                      const shortfall = Math.max(0, Math.abs(p.stressed.monthly_cash_flow));
                      navigate(`/rent-optimizer/${p.deal_id}?target=${Math.round(shortfall)}`);
                    }}
                    className="h-8 text-xs">{t('stressTest.exploreRevenue')}</Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Pill({ color, children, testid }) {
  const map = {
    red: 'bg-red-500/10 text-red-500',
    amber: 'bg-amber-500/10 text-amber-500',
    emerald: 'bg-emerald-500/10 text-emerald-500',
  };
  return <span data-testid={testid} className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${map[color]}`}>{children}</span>;
}

function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="h-32 bg-slate-100 dark:bg-slate-800 rounded-2xl" />
      <div className="h-40 bg-slate-100 dark:bg-slate-800 rounded-2xl" />
      <div className="h-64 bg-slate-100 dark:bg-slate-800 rounded-2xl" />
    </div>
  );
}

function Failed({ t, onRetry }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-6 text-center">
      <p className="font-semibold text-[#12355B] dark:text-white mb-2">{t('stressTest.failed')}</p>
      <Button onClick={onRetry} variant="outline" className="rounded-xl">{t('stressTest.retry')}</Button>
    </div>
  );
}

function EmptyPortfolio({ t, navigate }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824] p-12 text-center">
      <p className="font-semibold text-[#12355B] dark:text-white mb-2">{t('stressTest.empty.title')}</p>
      <p className="text-sm text-slate-500 mb-4">{t('stressTest.empty.subtitle')}</p>
      <Button data-testid="empty-analyze" onClick={() => navigate('/analyze')} className="bg-[#12355B] text-white rounded-xl">
        {t('stressTest.empty.cta')}
      </Button>
    </div>
  );
}

function Locked({ t, navigate }) {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <section data-testid="stress-locked" className="rounded-2xl bg-gradient-to-br from-[#12355B] to-[#1a4574] p-8 text-white shadow-card">
        <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-4">
          <Lock className="w-6 h-6" />
        </div>
        <h1 className="font-primary font-extrabold text-2xl md:text-3xl tracking-tight mb-2">{t('stressTest.title')}</h1>
        <p className="text-white/70 mb-6 max-w-lg">{t('stressTest.lockedSubtitle')}</p>
        <Button data-testid="stress-upgrade" onClick={() => {
          fireChatAnalytics('stress_test_upgrade_clicked', ANALYTICS_KEY, {});
          navigate('/pricing');
        }} className="bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B] rounded-xl h-11 px-6 font-bold">
          {t('stressTest.unlockCta')}
        </Button>
      </section>
    </div>
  );
}
